// The light, sticker-style identity shared with the native app
// (jace-games-app src/lib/theme.ts): a soft lilac ground, deep plum ink,
// 3px ink outlines + a hard offset shadow, in six bright fills. Canvas games
// import these instead of hard-coding colours; styles.css mirrors them as
// CSS custom properties.

export const C = {
  bg: "#F3F0FF",
  ink: "#1B1534",
  muted: "#5E5878",
  faint: "#8E86AE",
  line: "#D9D2F5",
  white: "#FFFFFF",
  green: "#37D67A", // the JACE dot
  mango: "#FFC53D",
  coral: "#FF6B5B",
  cobalt: "#4D7CFF",
  bubblegum: "#FF6CC4",
  mint: "#2ED3A6",
  grape: "#8B5CF6",
  danger: "#E5484D",
  /** deep night for space games — chrome stays white stickers on top */
  night: "#2A1F5C",
  nightDeep: "#1F1846",
} as const;

/** pale versions of the fills, for art and scene backgrounds */
export const TINT = {
  mango: "#FFE7A8",
  coral: "#FFD3CC",
  cobalt: "#CFDBFF",
  bubblegum: "#FFD1EC",
  mint: "#BDF3E3",
  grape: "#E1D4FF",
} as const;

/** player colours, by seat */
export const SEAT_COLORS = [C.coral, C.cobalt, C.mango, C.mint, C.grape] as const;

export const FONT_DISPLAY = `"Baloo 2", "Nunito", system-ui, sans-serif`;
export const FONT_BODY = `"Nunito", system-ui, sans-serif`;

/** canvas font shorthand: Baloo 2 800 for numbers/titles */
export const displayFont = (px: number) => `800 ${px}px ${FONT_DISPLAY}`;
/** canvas font shorthand: Nunito for small labels (weight 700/800/900) */
export const bodyFont = (px: number, weight = 800) => `${weight} ${px}px ${FONT_BODY}`;

/**
 * A white sticker pill for canvas HUDs (score, lives, timer): white fill, ink
 * outline, hard offset ink shadow. Returns the pill's width so callers can lay
 * several out in a row.
 */
export function hudPill(
  g: CanvasRenderingContext2D,
  x: number, y: number,
  text: string,
  opts: { align?: "left" | "center" | "right"; size?: number; fill?: string; ink?: string; label?: string } = {},
): number {
  const size = opts.size ?? 18;
  const h = Math.round(size * 1.9);
  g.save();
  g.font = displayFont(size);
  const labelFont = bodyFont(Math.round(size * 0.62), 900);
  let labelW = 0;
  if (opts.label) {
    g.font = labelFont;
    labelW = g.measureText(opts.label).width + size * 0.4;
    g.font = displayFont(size);
  }
  const tw = g.measureText(text).width;
  const w = Math.round(tw + labelW + size * 1.3);
  const left = opts.align === "center" ? x - w / 2 : opts.align === "right" ? x - w : x;
  const r = h / 2;
  // hard shadow
  g.fillStyle = C.ink;
  pillPath(g, left, y + 3, w, h, r);
  g.fill();
  g.fillStyle = opts.fill ?? C.white;
  pillPath(g, left, y, w, h, r);
  g.fill();
  g.lineWidth = 2.5;
  g.strokeStyle = C.ink;
  g.stroke();
  g.textBaseline = "middle";
  g.textAlign = "left";
  let cx = left + size * 0.65;
  if (opts.label) {
    g.font = labelFont;
    g.fillStyle = C.muted;
    g.fillText(opts.label, cx, y + h / 2 + 1);
    cx += labelW;
  }
  g.font = displayFont(size);
  g.fillStyle = opts.ink ?? C.ink;
  g.fillText(text, cx, y + h / 2 + size * 0.08);
  g.restore();
  return w;
}

function pillPath(g: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  const rr = Math.min(r, w / 2, h / 2);
  g.beginPath();
  g.moveTo(x + rr, y);
  g.arcTo(x + w, y, x + w, y + h, rr);
  g.arcTo(x + w, y + h, x, y + h, rr);
  g.arcTo(x, y + h, x, y, rr);
  g.arcTo(x, y, x + w, y, rr);
  g.closePath();
}
